import { Check } from 'lucide-react';
import { APPOINTMENT_STATUSES } from '@/lib/appointmentStatuses';
import { chipMetrics, chipRowClass, chipButtonClass } from '@/components/ChipRow';

/**
 * ---- ⭐⭐ THE STATUS KEY THAT IS ALSO THE STATUS FILTER — ONE COMPONENT ----
 *
 * Extracted from `pages/Calendar.tsx` (the strip at 2467–2532) on 2026-09-18 for
 * **decision 184**. ⭐ Decision 110 designed it; 184 narrowed where it renders.
 *
 * > Pete, 2026-09-13: *"We are needing more real estate for all the panels. Lets
 * > move the big filter, status bar right below calendar to the small status bar
 * > inbetween Sunday (date) and 2 appointments-23doors… more dock doors will show,
 * > more driver checkins will show and the right side panel."*
 *
 * ⛔ **PROPS, NOT CONTEXT.** Every value below already lives in `Calendar.tsx`,
 * which owns the filter and computes the counts. A context would let this render
 * anywhere and quietly disagree with the grid beside it.
 *
 * ---- ⛔⛤ WHAT THE MERGE ACTUALLY FIXES, WHICH IS NOT THE LAYOUT ----
 *
 * `GanttCalendar.tsx` drew its own version of this row, and it was wrong in three
 * ways that all look like styling until you read them:
 *
 *   1. ** IT HARDCODED ITS OWN SIX-ELEMENT ARRAY. ** The big bar's own comment
 *      says why that is a fault: *"ONE LIST, SHARED WITH THE SEARCH FILTER (#254)
 *      … copying them would have been two lists that must agree forever."* The
 *      Gantt row WAS that second list.
 *   2. ** IT OMITTED `BLOCK`. ** Six of seven. A door outage is the one thing on
 *      the grid that is not a truck, and the header never counted it.
 *   3. ** ITS COUNTS WERE COUNTS OF THE FILTERED SET. ** It received
 *      `appointments={filterAppts(...)}` and then ran
 *      `appointments.filter(a => a.statusCode === code)` over it — so filtering to
 *      "Approved" drove every other count in that header to 0.
 *
 *      ** A COUNT COMPUTED FROM WHAT SURVIVED THE FILTER CANNOT TELL YOU WHAT THE
 *         FILTER IS HIDING, WHICH IS THE ONLY QUESTION IT WAS ON SCREEN TO
 *         ANSWER. **
 *
 * ⭐ `statusCounts` and `totalCount` come from `Calendar.tsx`'s UNFILTERED source,
 * so the numbers here describe the day, not the current selection.
 *
 * ---- ⭐ `dense` ----
 *
 * > Pete, 2026-09-18: *"The top filter should obvioulsly be made the size of the
 * > fllter we are removing, inbetween the Date xxxxxxxxxxx 0 Appointments 67
 * > Doors"*
 *
 * `dense` shrinks the swatches and tightens the gaps to sit in the Gantt's header
 * strip. ⛔ It does NOT drop the checkbox affordance — that tick is the only thing
 * that says this is a control and not a legend, and the row it replaces was a
 * legend that looked identical.
 */
export interface StatusFilterBarProps {
    /** Codes currently selected. EMPTY MEANS "no filter", not "show nothing". */
    statusFilter: Set<string>;
    /** Toggle one code. Owned by Calendar.tsx so the grid and this row cannot disagree. */
    toggleStatus: (code: string) => void;
    /** Used only to clear back to "All Status". */
    setStatusFilter: (next: Set<string>) => void;
    /**
     * ---- ⭐⭐ COUNTS ARE OPTIONAL, AND THE OPTION IS A UNION (2026-09-20) ----------
     *
     * ⭐ Pete asked for the SEARCH's status strip to *"match (size) and checkboxes of
     * other status for consistency"*. ⛔ But the search has **no day** — it looks
     * across dates — so it has nothing to count, and a bar reading *"Requested 0"*
     * there would be a made-up fact rather than a missing one.
     *
     * ⭐⭐ AND THIS FILE ALREADY CARRIES THE RULE, three lines from where the board
     * renders it:
     *   ** IDENTICAL STYLING IS CONSISTENCY. IDENTICAL CONTENT WOULD BE A LIE ABOUT
     *      WHAT EACH VIEW CONTAINS. **
     *
     * ⛔ SO IT IS A DISCRIMINATED UNION, NOT AN OPTIONAL FLAG. Showing counts without
     * having them is **unrepresentable** rather than merely discouraged — the same
     * reasoning as `FK_UserCompanyRole_AppRole` being a composite FK instead of a
     * CHECK. ⚠️ A `showCounts?: boolean` beside two optional numbers would compile
     * perfectly with the numbers missing and render zeros.
     */

    /** Tighter sizing for the Gantt header strip. */
    dense?: boolean;
    /**
     * Status codes to leave OUT of the row.
     *
     * > Pete, 2026-09-19: *"you can make blocked invisible for now"*
     *
     * ⛔⛤ STATED AS A TENSION, NOT SLIPPED IN. 1AV.157 records that the Gantt
     * row this component replaced OMITTED `BLOCK` and calls that a FAULT -- a
     * door outage is the one thing on the grid that is not a truck, and the
     * header never counted it. ⭐ The difference is that this is now a NAMED,
     * DELIBERATE, per-caller suppression with "for now" attached, not seven
     * statuses hardcoded as six by accident.
     *
     * ⛔⛔ A HIDDEN STATUS THAT IS CURRENTLY SELECTED IS STILL RENDERED. See the
     * map below: hiding an ACTIVE filter leaves a user filtering by something
     * with no control to turn it off.
     */
    hideStatuses?: readonly string[];
    /**
     * ⚠️ OFF IN THE GANTT HEADER, AND DELIBERATELY. The Gantt already renders its
     * own `Limits` legend in the capacity-band row immediately below this one.
     * Drawing it twice in one header is precisely the duplication this component
     * exists to remove.
     */
    showOverlayLegend?: boolean;
    className?: string;
}

type StatusFilterBarCountProps =
    | {
        /** Omitted or true: the bar shows counts, and therefore MUST be given them. */
        showCounts?: true;
        /** Per-code counts over the UNFILTERED day. */
        statusCounts: Record<string, number>;
        /** Total over the UNFILTERED day — what "All Status (N)" shows. */
        totalCount: number;
    }
    | {
        /** ⛔ No counts exist for this surface. The chips render without numbers. */
        showCounts: false;
        statusCounts?: never;
        totalCount?: never;
    };

export type StatusFilterBarAllProps = StatusFilterBarProps & StatusFilterBarCountProps;
/**
 * ---- ⭐⭐ THE STRIP THE FILTER SITS IN, DEFINED ONCE (Pete, 2026-09-19) --------
 *
 * > Pete: *"Why doesn't it look the same and why is there space below the status
 * > bar on week and month? Should be formatted identical."*
 *
 * ⛔⛔ THE COMPONENT WAS ALREADY SHARED. THE CONTAINER WAS NOT. Day rendered the
 * bar as a tinted, bordered strip INSIDE the grid card; Week and Month rendered it
 * as a bare div on the PAGE, above their cards — full page width, no band, and an
 * `mb-3` gap underneath. Same markup, same props, three different frames.
 *
 *   ** DEDUPLICATING A COMPONENT DOES NOT DEDUPLICATE ITS SURROUNDINGS, AND THE
 *      SURROUNDINGS ARE MOST OF WHAT "LOOKS THE SAME" MEANS. **
 *
 * ⚠️ A STRING, NOT A WRAPPER COMPONENT, on purpose: the Gantt's strip already
 * exists and holds other things, so a wrapper would have meant re-parenting live
 * JSX to gain nothing. One `className` reference per board is the whole seam.
 */
/** ⭐ Decision 269: the calendar's one header, with the darker frame line (utils FRAME_BORDER). */
export const BOARD_HEADER_STRIP_CLASS = 'border-b border-stone-400 bg-muted/50 px-4 py-2 text-sm';


export function StatusFilterBar({
    statusFilter,
    toggleStatus,
    setStatusFilter,
    showCounts = true,
    statusCounts,
    totalCount,
    dense = false,
    hideStatuses,
    showOverlayLegend = true,
    className,
}: StatusFilterBarAllProps) {
    // ---- ⬛⤴ `dense` HAD A TERNARY WHOSE BRANCHES WERE EQUAL (fixed 2026-09-19) ----
    //
    // ⛔⛤ `const gapX = dense ? 'gap-x-2' : 'gap-x-2';` shipped on 2026-09-19. Two
    // identical branches: it reads as a decision and is a no-op.
    //
    //   ** A TERNARY WHOSE BRANCHES ARE EQUAL IS A DECISION THAT WAS NEVER MADE,
    //      WEARING THE CLOTHES OF ONE. ** And the source guard asserted only that
    //      `dense` was PASSED. Presence is not effect.
    //
    // ⭐ tenancy-row3-guard.cjs now fails on any `dense ? 'x' : 'x'` in this file.
    //
    // ---- ⭐ THE DENSE VALUES ARE THE ROW THIS REPLACED, MEASURED FROM GIT ----
    //
    // `git show ebac565^:.../GanttCalendar.tsx` — the deleted header row drew:
    //
    //     <span className="inline-block h-2 w-2 rounded-full" style={{...}} />
    //     <span className="text-muted-foreground">{STATUS_LABEL[code]}</span>
    //     <span className="font-semibold tabular-nums">{n}</span>
    //
    // ⛔ So the original swatch was an 8px ROUND DOT and the count carried NO
    // parentheses. This component shipped a 12px SQUARE and `(n)` — 50% larger,
    // a different shape, and two extra characters on every item.
    //
    // ⚠️ AND IT DRAWS EIGHT ITEMS WHERE THE OLD ROW DREW SIX: `Blocked` (the door
    // outage the old header never counted) and the `All Status` control. ⭐ Matching
    // the old sizing is what buys room for the two the old row was missing.
    /**
     * ---- ⛔⛔ THESE TOKENS LEFT THIS FILE (2026-09-19) --------------------------
     *
     * They now live in `components/ChipRow.tsx`, because the subject chips
     * (`SubjectChips`, decision 188) must be the same size and shape as these BY
     * CONSTRUCTION rather than by somebody matching them.
     *
     *   ** AN EXTRACTION THAT DOES NOT DELETE THE ORIGINAL IS A COPY. ** §1AV.170
     *   spent a day and a half on that exact failure — this component was itself
     *   extracted out of `Calendar.tsx` and the original was left behind.
     *
     * ⛔ DO NOT RE-DECLARE THEM HERE, not even "just one". A guard fails the build
     * if this file contains a `dense ? ... : ...` metric again.
     * ⚠️ `gapX` is deliberately NOT destructured — it belongs to the ROW, and
     * `chipRowClass` applies it. `noUnusedLocals` would fail the build on it anyway,
     * which is the compiler agreeing.
     */
    const { swatch, round, tick, gapIn, pad, ring } = chipMetrics(dense);
    // ⛔⤴ AND THE SWATCH IS SIZED FOR A TICK, NOT FOR THE OLD DOT (2026-09-19).
    //
    // The first fix set dense to `h-2 w-2 rounded-full` to match the row this
    // replaced -- and then had to HIDE the tick, because a glyph inside an 8px
    // circle is a smudge.
    //
    //   ** I MATCHED THE THING IT REPLACED INSTEAD OF THE THING IT BECAME. **
    //   The old row was a LEGEND: a dot only has to be SEEN. This is a FILTER:
    //   its marker has to be readable as ON or OFF. Sizing the marker to the
    //   legend, then removing the only mark that distinguishes on from off to
    //   defend that size, is the tail wagging the dog.
    //
    // > Pete, 2026-09-19: *"Status other than All Status, need check marks in the
    // > little circles (like All Status has) so you know which ones are selected"*
    //
    // ⭐ So every swatch is the SAME SIZE as the All Status box, deliberately --
    // one `swatch` for both, not two names for one value.

    return (
        <div className={chipRowClass('status', dense, className)}>
            <button
                type="button"
                onClick={() => setStatusFilter(new Set())}
                title="Show all statuses"
                /**
                  * ---- ⭐⭐ `All Status` IS THE ONE CHIP THAT KEEPS ITS OLD TREATMENT ----
                  *
                  * ⭐ Pete, 2026-09-19: *"Lets keep All Status the same size, font, the
                  * rest the 10x"* — after asking for the row to shrink, and then looking
                  * at it shrunk.
                  *
                  * ⛔ SO THIS CHIP KEEPS `font-medium` WHILE EVERY OTHER CHIP IN THE ROW
                  * IS WEIGHTLESS. That is not an inconsistency that escaped — it is the
                  * row's zero state, and it is the control a scheduler reaches for to undo
                  * whatever the others did.
                  *
                  *   ** THE CONTROL THAT CLEARS A ROW IS NOT A PEER OF THE THINGS IT
                  *      CLEARS. IT IS THE WAY BACK, AND IT IS ALLOWED TO LOOK LIKE IT. **
                  *
                  * ⛔⤴ ~~`text-[11px]`~~ REMOVED FROM THIS BUTTON. It was added when the row
                  * briefly went to 10px, to hold this one chip back at its old size. The row
                  * is 11px again, so the class became **a duplicate of the inherited value**
                  * — present, correct, and doing nothing.
                  *   ** PRESENCE IS NOT EFFECT (§1AV.170). A CLASS THAT RESTATES WHAT IS
                  *      ALREADY INHERITED SURVIVES EVERY REVIEW AND MISLEADS THE NEXT
                  *      PERSON ABOUT WHERE THE SIZE IS DECIDED. **
                  * ⚠️ So this chip is now **byte-for-byte what it always was**: the row's
                  * 11px, `font-medium`, and a `font-semibold` count.
                  */
                className={chipButtonClass('status', dense, 'font-medium text-foreground/80 hover:bg-muted')}
            >
                <span
                    className={`inline-flex ${swatch} items-center justify-center rounded-sm border ${
                        statusFilter.size === 0 ? 'bg-foreground border-transparent' : 'border-input bg-background'
                    }`}
                >
                    {statusFilter.size === 0 && <Check className={`${tick} text-background`} strokeWidth={3} />}
                </span>
                {/* ⭐ THE COUNT MATCHES WHAT IS DRAWN — all of it. That is the
                    whole reason this reads "All Status" again. */}
                All Status{dense
                    /* ⛔ AND ITS COUNT KEEPS `font-semibold` TOO — *"the same size,
                       font"* means the chip as a whole, not just its label. The
                       per-status counts below are weightless. */
                    /* ⛔ NOTHING, not a zero. A surface with no day to count over
                       renders the word alone — see the union on the props. */
                    ? (showCounts && <span className="font-semibold tabular-nums">{totalCount}</span>)
                    : (showCounts ? ` (${totalCount})` : '')}
            </button>
            {/**
              * ⛔ ONE LIST, SHARED WITH THE SEARCH FILTER (#254). This was an
              * inline array in Calendar.tsx; the search panel needed the same seven,
              * and copying them would have been two lists that must agree forever —
              * the exact fault `matchedOn` is computed server-side to avoid.
              * ⛔⛤ AND GanttCalendar.tsx HAD COPIED IT ANYWAY, minus BLOCK. That is
              * what this component ends.
              */}
            {APPOINTMENT_STATUSES.map(({ label, swatch: cls, code }) => {
                const checked = statusFilter.has(code);
                // ⛔⛔ NEVER HIDE A CONTROL THAT IS CURRENTLY DOING SOMETHING.
                //
                // `hideStatuses` removes an OPTION, not an ACTIVE FILTER. Select
                // Blocked on Week (where it shows), switch to Day (where it does
                // not), and without this you are filtering by a status with no
                // control on screen to turn it off -- an empty grid and nothing
                // that explains it. `All Status` would clear it, but only for
                // somebody who already guessed what was wrong.
                if (!checked && hideStatuses?.includes(code)) return null;
                return (
                    <button
                        key={label}
                        type="button"
                        onClick={() => toggleStatus(code)}
                        title={checked ? `Hide ${label}` : `Show ${label}`}
                        /* ⛔ NO WEIGHT — see the All Status chip above. */
                        className={chipButtonClass('status', dense, 'hover:bg-muted')}
                    >
                        <span
                            // ⚠️ BLOCK STAYS SQUARE EVEN WHEN THE REST ARE DOTS. Its 45-degree
                            // hatch is unreadable on an 8px circle, and a door outage being
                            // the one marker shaped unlike the trucks is honest, not untidy.
                            className={`inline-flex ${swatch} items-center justify-center ${
                                code === 'BLOCK' ? 'rounded-sm' : round
                            } ${cls} ${checked ? ring : 'opacity-60'}`}
                            // A legend swatch has one job: look like the thing it
                            // names. Blocked was a flat grey square while a block on
                            // the grid is grey WITH a 45-degree hatch, so the one
                            // marker that means "this is not an appointment" didn't
                            // match itself. Same angle and spacing as the block.
                            style={code === 'BLOCK' ? {
                                backgroundImage: 'repeating-linear-gradient(45deg, rgba(30,41,59,0.55) 0, rgba(30,41,59,0.55) 2px, transparent 2px, transparent 6px)',
                            } : undefined}
                        >
                            {/* ⭐ THE TICK IS THE WHOLE POINT. Without it a selected swatch and
                                an unselected one differ only by opacity and a hairline ring,
                                which is a difference you have to already know to see. */}
                            {checked && <Check className={`${tick} text-white`} strokeWidth={3} />}
                        </span>
                        {label}{dense
                            ? (showCounts && <span className="tabular-nums">{statusCounts?.[code] ?? 0}</span>)
                            : (showCounts ? ` (${statusCounts?.[code] ?? 0})` : '')}
                    </button>
                );
            })}
            {/**
              * ---- ⭐ THE OVERLAY LEGEND IS **LIMITS ONLY** NOW (2026-09-19) ----
              *
              * > Pete: *"Put the Limits purple icon back on the top bar and put the
              * > dedicated yellow bar next to DOORS on the first line of the calendar"*
              *
              * ⛔⛤ ~~`Dedicated` LEFT THIS COMPONENT — it did not disappear. It is
              * drawn beside the `Door` column header in GanttCalendar.tsx~~ — **THAT
              * SENTENCE WAS FALSE WHEN IT WAS WRITTEN.** GanttCalendar.tsx L1539 says
              * that version *"NEVER SHIPPED"*. For one day this comment asserted the
              * existence of a legend that was nowhere in the codebase.
              *   ** §4.1: AN ABSENCE CHECK MUST READ CODE, NOT COMMENTS — AND A
              *      COMMENT DESCRIBING WHERE SOMETHING *WENT* IS THE EASIEST KIND TO
              *      LEAVE BEHIND WHEN IT DID NOT GO THERE. **
              * ⚠️ If it ever needs to come back here, it comes back as one entry in
              * this array — not as a second copy beside the one in the Gantt.
              *
              * ⛔ NEITHER IS A FILTER. Both are legend: they name a colour drawn on the
              * grid by something that is not an appointment, and there is nothing to
              * toggle. That is why they carry no tick and no ring.
              */}
            {/**
              * ---- ⭐ `Dedicated` IS BACK (Pete, 2026-09-19) ------------------------
              *
              * > *"Please make Dedicated visible again on the status bar since there is
              * > room"*
              *
              * ⚠️ AND HE REVERSED HIS OWN CALL FROM EARLIER THE SAME DAY, WHICH IS HIS
              * TO REVERSE — but the original reason was **not** space. It was struck
              * because the grid already spells a dedication out in words three times:
              * the window band, the drag handle carrying the carrier's name, and the
              * door-label badge reading `{door.dedicatedTo}-Dedicated`.
              *   ** A LEGEND FOR SOMETHING THE GRID ALREADY SPELLS OUT IN WORDS IS
              *      WALLPAPER (§269). ** That argument still stands on Day.
              * ⭐ WHAT CHANGED IS THE SCOPE, AND IT MAKES HIM RIGHT: this row now
              * renders on **Week and Month too**, where there is no band, no handle and
              * no badge — only an amber tint on a dedicated door. On those two views
              * the colour is written nowhere, so the key earns its pixels exactly the
              * way `Limits` does.
              *
              * ⛔ `bg-amber-300` MIRRORS `Limits`' CHOICE, NOT A GUESS. `Limits` uses
              * `bg-violet-300`, the BAND colour. The dedication band is `bg-amber-200/35`
              * with an `amber-400/40` edge and a `bg-amber-300/70` handle over it —
              * amber-300 is what a scheduler actually sees. ⚠️ NOT `bg-amber-500`: that
              * is the dot inside the door badge, a different element at a different size.
              */}
            {showOverlayLegend && ([
                ['Limits', 'bg-violet-300'],
                ['Dedicated', 'bg-amber-300'],
            ] as [string, string][]).map(([label, cls]) => (
                <span key={label} className={`inline-flex items-center ${gapIn} whitespace-nowrap ${pad}`}>
                    <span className={`inline-block ${swatch} ${round} ${cls}`} />
                    {label}
                </span>
            ))}
        </div>
    );
}

export default StatusFilterBar;
